import { apiFetch } from '../utils/api';
import { ProductVariant } from './productVariants';

export type ProductMasterType = 'regular' | 'service' | (string & {});

export interface ProductMasterBrandSummary {
  id: string;
  name: string | null;
  erpId: number | null;
}

export interface ProductMasterTemplateSummary {
  id: string;
  name: string | null;
  erpId: number | null;
}

export interface ProductMaster {
  id: string;
  erpId: number;
  name: string;
  description?: string | null;
  articleNumber: string;
  companyErpId: number | null;
  brandErpId: number | null;
  vendorErpId: number | null;
  variantTemplateErpId: number | null;
  originalCreatedAt: string | null;
  type: ProductMasterType;
  isCreatedByWorkshopManagement: boolean;
   isActive: boolean;
  createdAt: string;
  updatedAt: string;
  brand?: ProductMasterBrandSummary | null;
  variantTemplate?: ProductMasterTemplateSummary | null;
  variants?: ProductVariant[];
  categories?: { id: string; name: string; erpId: number }[];
  category_ids?: number[];
}

export interface WorkshopProductVariantValueInput {
  id: number;
  selectedValue: string;
}

export interface WorkshopProductVariantInput {
  erpId?: number;
  name: string;
  code?: string;
  description?: string;
  upc?: string;
  retailPrice?: string;
  staffPrice?: string;
  isTemporarilyUnavailable?: boolean;
  isAvailableInPos?: boolean;
  isAvailableInEcommerce?: boolean;
  isSoldAsSingleItem?: boolean;
  thumbnail?: string;
  productVariantValues?: WorkshopProductVariantValueInput[];
}

export interface WorkshopProductInput {
  erpId?: number;
  name: string;
  description?: string;
  code?: string;
  brandId?: number;
  variantTemplateId?: number;
  categoryIds?: number[];
  articleNumber: string;
  externalMasterProductId?: string;
  isWarranty?: boolean;
  hasBatch?: boolean;
  typeId?: number;
  type?: ProductMasterType;
  isNonInventory?: boolean;
  originalCreatedAt?: string;
  variants: WorkshopProductVariantInput[];
}

export interface PaginatedMastersResponse {
  data: ProductMaster[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface FetchMastersParams {
  page?: number;
  limit?: number;
  search?: string;
  createdByWorkshopManagement?: boolean;
  isActive?: boolean;
}

export const fetchProductMasters = (
  params: FetchMastersParams = {},
): Promise<PaginatedMastersResponse> => {
  const searchParams = new URLSearchParams();

  if (params.page) {
    searchParams.set('page', String(params.page));
  }

  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }

  if (params.search) {
    searchParams.set('search', params.search);
  }

  if (params.createdByWorkshopManagement !== undefined) {
    searchParams.set('createdByWorkshopManagement', String(params.createdByWorkshopManagement));
  }

  if (params.isActive !== undefined) {
    searchParams.set('isActive', String(params.isActive));
  }

  const query = searchParams.toString();
  return apiFetch<PaginatedMastersResponse>(
    `/product/masters${query ? `?${query}` : ''}`,
  );
};

export const fetchProductMaster = (id: string) =>
  apiFetch<ProductMaster>(`/product/masters/${id}`);

export const createWorkshopProduct = (input: WorkshopProductInput) =>
  apiFetch<ProductMaster>(`/product/masters/workshop`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateWorkshopProduct = (erpId: number, input: WorkshopProductInput) =>
  apiFetch<ProductMaster>(`/product/masters/workshop/${erpId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });

export const updateProductMasterActive = (id: string, isActive: boolean) =>
  apiFetch<ProductMaster>(`/product/masters/${id}/active`, {
    method: 'PUT',
    body: JSON.stringify({ isActive }),
  });
